import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CaretDownOutlined } from '@ant-design/icons';
import { useQueries } from '@tanstack/react-query';
import { TreeSelect } from 'antd';

/**
 * Filtro de assuntos em árvore, no modelo do QConcursos.
 *
 * Um único campo reúne disciplina (cabeçalho), assuntos (pais) e delimitações
 * (filhas). Cada assunto é numerado (1, 2, 3...) e cada delimitação herda o
 * número do pai (1.1, 1.2, 2.1...), refletindo a ordem definida no cadastro. As
 * linhas de árvore mostram a hierarquia e tudo abre expandido.
 *
 * Marcar o assunto inteiro filtra por ele; marcar delimitações específicas
 * filtra só por elas - é a semântica de `SHOW_PARENT`. Filtrar por um assunto
 * alcança também as questões classificadas só numa delimitação dele: a inclusão
 * da hierarquia mora no backend, então aqui o pai sozinho basta.
 *
 * O componente não conhece a API: recebe uma função que carrega a árvore de uma
 * disciplina e cuida do cache com React Query. Assim serve tanto ao app quanto
 * ao admin, e tanto a uma disciplina (filtro do aluno) quanto a várias (cadastro
 * de questão, que associa N disciplinas).
 */

export interface AssuntoNode {
  id: string;
  name: string;
  details: { id: string; name: string }[];
}

export type ArvoreDeAssuntos = AssuntoNode[];

/** O que sai do filtro: assuntos e delimitações escolhidos, separados. */
export interface SelecaoDeAssuntos {
  subjectIds: string[];
  subjectDetailIds: string[];
}

export interface AssuntoTreeSelectProps {
  /** Disciplinas cujas árvores entram no campo. Uma no app, N no cadastro. */
  disciplineIds: string[];
  carregarArvore: (disciplineId: string) => Promise<ArvoreDeAssuntos>;
  /** Controlado. Opcional para casar com o `Form.Item` do Ant Design. */
  value?: SelecaoDeAssuntos;
  /** Devolve a seleção e um mapa id->nome dos itens marcados, para os chips. */
  onChange?: (selecao: SelecaoDeAssuntos, nomes: Record<string, string>) => void;
  /** Nome de cada disciplina, usado no cabeçalho de grupo. */
  nomeDaDisciplina?: (id: string) => string | undefined;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

interface NoDaArvore {
  value: string;
  /** O que aparece na linha: o rótulo com rolagem no hover. */
  title: ReactNode;
  /** Texto puro do rótulo, para busca e para a tag do selecionado. */
  searchText: string;
  selectable?: boolean;
  checkable?: boolean;
  children?: NoDaArvore[];
}

const VAZIO: SelecaoDeAssuntos = { subjectIds: [], subjectDetailIds: [] };

/**
 * Rótulo que trunca o texto longo e, no hover, rola na horizontal para revelar
 * o resto - a delimitação costuma ser uma frase inteira e, sem isto, esticaria
 * o dropdown e quebraria o layout. `inline-block` com `max-width` garante o
 * corte sem depender do layout interno da árvore do Ant Design.
 */
function RotuloRolante({ texto }: { texto: string }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [deslocamento, setDeslocamento] = useState(0);
  const [duracao, setDuracao] = useState(0);

  const aoEntrar = () => {
    const container = containerRef.current;
    const conteudo = container?.firstElementChild as HTMLElement | null | undefined;
    if (!container || !conteudo) return;
    const excedente = conteudo.scrollWidth - container.clientWidth;
    if (excedente > 1) {
      setDeslocamento(excedente);
      // ~60px/s dá para acompanhar a leitura, com um teto para textos enormes.
      setDuracao(Math.min(6, excedente / 60));
    }
  };

  return (
    <span
      ref={containerRef}
      onMouseEnter={aoEntrar}
      onMouseLeave={() => setDeslocamento(0)}
      style={{
        display: 'inline-block',
        maxWidth: 'clamp(160px, 60vw, 300px)',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          transform: `translateX(${-deslocamento}px)`,
          transition: `transform ${duracao}s linear`,
        }}
      >
        {texto}
      </span>
    </span>
  );
}

export function AssuntoTreeSelect({
  disciplineIds,
  carregarArvore,
  value = VAZIO,
  onChange,
  nomeDaDisciplina,
  placeholder,
  disabled,
  style,
}: AssuntoTreeSelectProps) {
  const consultas = useQueries({
    queries: disciplineIds.map((disciplinaId) => ({
      queryKey: ['subject-tree', disciplinaId],
      queryFn: () => carregarArvore(disciplinaId),
    })),
  });

  const carregando = consultas.some((consulta) => consulta.isFetching);

  /*
   * Todas as árvores em mãos. É a condição para podar: com uma disciplina ainda
   * sem resposta - carregando, ou com a consulta falhada -, o que se sabe sobre
   * os assuntos dela é nada, e podar contra o nada apaga a classificação inteira
   * da questão. Some da tela, entra como alteração pendente e, se alguém salvar,
   * some do banco.
   */
  const arvoresCarregadas = consultas.every((consulta) => consulta.isSuccess);

  const idsDeAssunto = new Set<string>();
  const nomePorId: Record<string, string> = {};
  // Nós que têm filhos - a disciplina e os assuntos - para começarem abertos.
  const chavesExpansiveis: string[] = [];

  // A disciplina é sempre o cabeçalho do grupo (não marcável), como no
  // QConcursos: dentro dele vêm os assuntos numerados e suas delimitações.
  const treeData: NoDaArvore[] = disciplineIds.map((disciplinaId, indiceDisc) => {
    const arvore = consultas[indiceDisc]?.data ?? [];
    const chaveDisciplina = `disc:${disciplinaId}`;
    chavesExpansiveis.push(chaveDisciplina);

    const assuntos: NoDaArvore[] = arvore.map((assunto, indiceAssunto) => {
      idsDeAssunto.add(assunto.id);
      nomePorId[assunto.id] = assunto.name;
      chavesExpansiveis.push(assunto.id);
      const numeroAssunto = indiceAssunto + 1;
      const rotuloAssunto = `${numeroAssunto} - ${assunto.name}`;

      return {
        value: assunto.id,
        title: <RotuloRolante texto={rotuloAssunto} />,
        searchText: rotuloAssunto,
        children: assunto.details.map((delimitacao, indiceDetalhe) => {
          nomePorId[delimitacao.id] = delimitacao.name;
          const rotuloDelimitacao = `${numeroAssunto}.${indiceDetalhe + 1} - ${delimitacao.name}`;
          return {
            value: delimitacao.id,
            title: <RotuloRolante texto={rotuloDelimitacao} />,
            searchText: rotuloDelimitacao,
          };
        }),
      };
    });

    const nomeDisciplina = nomeDaDisciplina?.(disciplinaId) ?? 'Disciplina';
    return {
      value: chaveDisciplina,
      title: <RotuloRolante texto={nomeDisciplina} />,
      searchText: nomeDisciplina,
      selectable: false,
      checkable: false,
      children: assuntos,
    };
  });

  const subjectIds = value.subjectIds ?? [];
  const subjectDetailIds = value.subjectDetailIds ?? [];

  // Tirar uma disciplina apaga da árvore os assuntos dela; a seleção que ficou
  // órfã precisa cair junto, senão a questão guardaria assunto sem disciplina.
  const assinaturaConhecidos = [...idsDeAssunto].sort().join(',');
  useEffect(() => {
    // Sem disciplina não há árvore para comparar; podar aqui apagaria a seleção
    // por engano (ex.: enquanto o formulário ainda carrega a questão).
    if (!arvoresCarregadas || !onChange || disciplineIds.length === 0) return;
    const conhecido = (id: string) => idsDeAssunto.has(id) || nomePorId[id] !== undefined;
    const s = subjectIds.filter(conhecido);
    const d = subjectDetailIds.filter(conhecido);
    if (s.length !== subjectIds.length || d.length !== subjectDetailIds.length) {
      const nomes: Record<string, string> = {};
      for (const id of [...s, ...d]) if (nomePorId[id]) nomes[id] = nomePorId[id];
      onChange({ subjectIds: s, subjectDetailIds: d }, nomes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arvoresCarregadas, assinaturaConhecidos, subjectIds.join(','), subjectDetailIds.join(',')]);

  // Abre tudo por padrão. `treeDefaultExpandAll` não pega os nós que chegam
  // depois (a árvore vem assíncrona), então controlamos as chaves: cada nó novo
  // entra aberto uma vez; colapsar depois é escolha do usuário e é respeitado.
  const [expandidas, setExpandidas] = useState<string[]>([]);
  const jaAbertas = useRef<Set<string>>(new Set());
  const assinaturaExpansiveis = chavesExpansiveis.join(',');
  useEffect(() => {
    const novas = chavesExpansiveis.filter((chave) => !jaAbertas.current.has(chave));
    if (novas.length === 0) return;
    novas.forEach((chave) => jaAbertas.current.add(chave));
    setExpandidas((atuais) => [...new Set([...atuais, ...novas])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinaturaExpansiveis]);

  const valorAtual = [...subjectIds, ...subjectDetailIds];

  const aoMudar = (selecionados: string[]) => {
    const proximoSubjectIds: string[] = [];
    const proximoDetailIds: string[] = [];
    const nomes: Record<string, string> = {};

    for (const id of selecionados) {
      if (id.startsWith('disc:')) continue;
      if (idsDeAssunto.has(id)) proximoSubjectIds.push(id);
      else proximoDetailIds.push(id);
      if (nomePorId[id]) nomes[id] = nomePorId[id];
    }

    onChange?.({ subjectIds: proximoSubjectIds, subjectDetailIds: proximoDetailIds }, nomes);
  };

  return (
    <TreeSelect
      treeCheckable
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeData={treeData}
      value={valorAtual}
      onChange={aoMudar}
      treeExpandedKeys={expandidas}
      onTreeExpand={(chaves) => setExpandidas(chaves as string[])}
      // A linha de árvore mostra a hierarquia, como no QConcursos.
      treeLine={{ showLeafIcon: false }}
      // O ícone padrão da linha de árvore destoa do tema; usamos o mesmo caret
      // discreto das demais árvores do Ant Design (folha não tem seta).
      switcherIcon={(props: { expanded?: boolean; isLeaf?: boolean }) =>
        props.isLeaf ? null : <CaretDownOutlined rotate={props.expanded ? 0 : -90} />
      }
      disabled={disabled || disciplineIds.length === 0}
      loading={carregando}
      placeholder={placeholder}
      showSearch
      // O rótulo é ReactNode (rola no hover); busca e tag usam o texto puro.
      treeNodeFilterProp="searchText"
      treeNodeLabelProp="searchText"
      allowClear
      maxTagCount="responsive"
      // Rótulo já vem truncado, então o dropdown não estica sem limite; o teto
      // por viewport garante que nunca ultrapasse a tela no celular.
      popupMatchSelectWidth={false}
      styles={{ popup: { root: { maxWidth: '95vw' } } }}
      style={{ width: '100%', ...style }}
    />
  );
}
